import { warn } from './warn';

import type {
  CarrotQuest,
  CarrotQuestMethod,
  CarrotQuestMethodName,
  CarrotQuestProperties,
} from '../CarrotQuestManager.types';

const CDN_URL = 'https://cdn.carrotquest.app';
const PROXY_IFRAME_ID = 'proxy-iframe';
const PROXY_IFRAME_STYLE =
  'display: block !important; width: 0 !important; height: 0 !important; border: none !important; position: absolute !important; top: 0 !important; left: 0 !important; visibility: hidden !important;';
const SCRIPT_CHUNKS = ['chat', 'launcher', 'notification', 'tooltip'];
const QUEUED_METHOD_NAMES: CarrotQuestMethodName[] = [
  'connect',
  'track',
  'identify',
  'auth',
  'oth',
  'onReady',
  'addCallback',
  'removeCallback',
  'trackMessageInteraction',
];

const createQueueStub = (): CarrotQuest => {
  // Контракт вендора: бандл с CDN забирает очередь из глобальной `carrotquestasync`.
  // eslint-disable-next-line unicorn/no-global-object-property-assignment
  globalThis.carrotquestasync = [];

  const queuedMethods: Record<string, CarrotQuestMethod> = {};
  for (const methodName of QUEUED_METHOD_NAMES) {
    queuedMethods[methodName] = (...parameters: unknown[]) => {
      globalThis.carrotquestasync?.push(methodName, parameters);
    };
  }

  return { settings: {}, ...queuedMethods } as CarrotQuest;
};

const appendModuleScript = (
  proxyDocument: Document,
  source: string,
  isAsync = false,
) => {
  const scriptElement = proxyDocument.createElement('script');
  scriptElement.async = isAsync;
  scriptElement.type = 'module';
  scriptElement.src = source;
  proxyDocument.head.append(scriptElement);

  return scriptElement;
};

/**
 * Признак «бандл уже запрашивали» — сам iframe в DOM, а не наличие
 * `globalThis.carrotquest`: заглушка появляется раньше загрузки и переживает
 * её провал, поэтому по ней нельзя судить, поднялся ли виджет.
 */
const isBundleRequested = () => Boolean(document.querySelector(`#${PROXY_IFRAME_ID}`));

/**
 * Бандл не загрузился — убираем iframe. Очередь `carrotquestasync` сохраняем:
 * следующая инициализация (ремоунт менеджера при логине с публичной страницы)
 * создаст iframe заново, и вендорный бандл проиграет накопленные вызовы.
 */
const handleBundleFailure = (proxyIframe: HTMLIFrameElement) => {
  warn(`failed to load ${CDN_URL}/index.js`);
  proxyIframe.remove();
};

const handleProxyIframeLoad = (proxyIframe: HTMLIFrameElement) => {
  try {
    const proxyDocument = proxyIframe.contentWindow?.document;
    if (!proxyDocument) {
      return;
    }

    const indexScript = appendModuleScript(
      proxyDocument,
      `${CDN_URL}/index.js`,
      true,
    );
    indexScript.addEventListener(
      'error',
      () => handleBundleFailure(proxyIframe),
      { once: true },
    );

    for (const chunk of SCRIPT_CHUNKS) {
      appendModuleScript(proxyDocument, `${CDN_URL}/${chunk}.js`);
    }
  } catch (error) {
    warn(`iframe load error: ${error}`);
  }
};

const mountProxyIframe = (proxyIframe: HTMLIFrameElement) => {
  if (document.readyState !== 'loading') {
    document.body.append(proxyIframe);
    return;
  }

  let isInserted = false;
  document.addEventListener('readystatechange', () => {
    if (document.readyState === 'loading' || isInserted) {
      return;
    }

    isInserted = true;
    document.body.append(proxyIframe);
  });
};

/**
 * Бандл виджета грузится не в страницу, а в скрытый iframe — так вендор
 * изолирует свои стили и скрипты от приложения.
 */
const createProxyIframe = () => {
  const proxyIframe = document.createElement('iframe');
  proxyIframe.setAttribute('id', PROXY_IFRAME_ID);
  proxyIframe.setAttribute('name', PROXY_IFRAME_ID);
  proxyIframe.setAttribute('style', PROXY_IFRAME_STYLE);
  proxyIframe.addEventListener(
    'load',
    () => handleProxyIframeLoad(proxyIframe),
    { once: true },
  );

  mountProxyIframe(proxyIframe);
};

/**
 * Моста `dashlyExecuteEval` здесь НЕТ намеренно (AF-536).
 *
 * Вендор ищет его в глобальной родителя и гонит через него код из поля
 * `settings.js_code` админки CarrotQuest. Под нашей политикой (`script-src`
 * без `'unsafe-eval'`, enforce с 03.08.2026) вызов `eval` не может завершиться
 * успехом НИ РАЗУ — мост мёртв по построению, а каждая загрузка виджета
 * порождала CSP-репорт: 7082 события / 657 пользователей в `flow-analist`.
 *
 * Почему это не потеря функциональности: относящийся к `ai.iidf.ru` код из
 * `js_code` уже переехал в бандл (`trackingEvents.ts`, `metricaGoals.ts`,
 * AI-102). Вендор зовёт мост внутри `try/catch`, поэтому его отсутствие даёт
 * пойманный `TypeError` вместо `EvalError` — и ни одного репорта.
 *
 * Вред был не в объёме, а в слепоте: Sentry не извлекает host из
 * `eval`-нарушений и складывает их в одну безымянную группу, поэтому наш шум
 * скрывал бы чужое eval-нарушение (csp.md § 5).
 *
 * Восстанавливать мост «ради совместимости» нельзя — сначала снимается
 * ai-часть из `js_code` в кабинете, иначе события задвоятся.
 */

/**
 * Поднимает CarrotQuest без inline-скрипта: код инициализации едет в бандле
 * приложения (`script-src 'self'`), а бандл виджета подключается внешними
 * тегами с уже разрешённого `cdn.carrotquest.app`. Поэтому политике не нужен
 * `'unsafe-inline'`.
 *
 * @example
 * initCarrotQuest({ id: fund.cq_api_key });
 */
export const initCarrotQuest = ({ id }: CarrotQuestProperties) => {
  if (!id) {
    warn('CarrotQuest Id is required');
    return;
  }

  if (!globalThis.carrotquest) {
    // Контракт вендора: и бандл, и наш код зовут виджет через глобальную `carrotquest`.
    // eslint-disable-next-line unicorn/no-global-object-property-assignment
    globalThis.carrotquest = createQueueStub();
  }

  if (!isBundleRequested()) {
    createProxyIframe();
  }

  globalThis.carrotquest.connect(id);
};
